import {
  Box,
  TabIndicator,
  Text,
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AllOrder from './AllOrder';
import OutForDelivery from './OutForDelivery';
import Shipped from './Shipped';
import Delivered from './Delivered';
import FailedDelivery from './FailedDelivery';
import Cancelled from './Cancelled';
import Returned from './Returned';
import ConfirmedOrders from './ConfirmedOrders';
import UnderMaintenance from './UnderMaintenance';
import ReadyForDelivery from './ReadyForDelivery';
import InWarehouse from './InWarehouse';
import withAuth from '@/HOCs/withAuth';
import { useGetStatusCounts } from '@/services/OrderManagement';

const STATUS_TO_TAB_KEY: Record<string, keyof TabCounts> = {
  'Order Confirmed': 'confirmedOrders',
  'Under Maintainance': 'underMaintenance',
  'Ready for Delivery': 'readyForDelivery',
  Shipped: 'shipped',
  'In Warehouse': 'isInWarehouse',
  'Out for Delivery': 'outForDelivery',
  Delivered: 'delivered',
  'Delivery Failed': 'failedDelivery',
  Cancelled: 'cancelled',
  Returned: 'returned',
};

interface TabCounts {
  allOrders: number;
  confirmedOrders: number;
  underMaintenance: number;
  readyForDelivery: number;
  shipped: number;
  isInWarehouse: number;
  outForDelivery: number;
  delivered: number;
  failedDelivery: number;
  cancelled: number;
  returned: number;
}

interface FilterState {
  start_date?: string;
  end_date?: string;
  category?: string;
  subcategory?: string;
  assign_title?: string;
  user_status?: boolean | null;
}

const AllOrders = () => {
  const { state } = useLocation();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [sharedFilters, setSharedFilters] = useState<FilterState>({
    start_date: '',
    end_date: '',
    category: '',
    subcategory: '',
    assign_title: '',
    user_status: null,
  });

  const { data: apiCounts = [], isLoading: countsLoading } = useGetStatusCounts(sharedFilters);

  const tabCounts = useMemo<TabCounts>(() => {
    const base: TabCounts = {
      allOrders: 0,
      confirmedOrders: 0,
      underMaintenance: 0,
      readyForDelivery: 0,
      shipped: 0,
      isInWarehouse: 0,
      outForDelivery: 0,
      delivered: 0,
      failedDelivery: 0,
      cancelled: 0,
      returned: 0,
    };

    // All Orders: sum transaction_count instead of order_count
    base.allOrders = apiCounts.reduce((sum, i) => sum + (i.transaction_count ?? 0), 0);

    apiCounts.forEach(({ status_name, order_count }) => {
      const key = STATUS_TO_TAB_KEY[status_name];
      if (key) base[key] = order_count;
    });

    return base;
  }, [apiCounts]);

  useEffect(() => {
    if (state?.tabIndex !== undefined && state.tabIndex !== selectedTabIndex) {
      setSelectedTabIndex(state.tabIndex);
    }
  }, [state?.tabIndex, selectedTabIndex]);

  const tabs = useMemo(
    () => [
      {
        label: 'All Orders',
        key: 'allOrders' as const,
        content: () => <AllOrder filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Pending Orders',
        key: 'confirmedOrders' as const,
        content: () => <ConfirmedOrders filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Under Manufacture',
        key: 'underMaintenance' as const,
        content: () => <UnderMaintenance filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Ready For Delivery',
        key: 'readyForDelivery' as const,
        content: () => <ReadyForDelivery filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Picked Up',
        key: 'shipped' as const,
        content: () => <Shipped filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'In Warehouse',
        key: 'isInWarehouse' as const,
        content: () => <InWarehouse filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Out For Delivery',
        key: 'outForDelivery' as const,
        content: () => <OutForDelivery filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Delivered',
        key: 'delivered' as const,
        content: () => <Delivered filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Failed Delivery',
        key: 'failedDelivery' as const,
        content: () => <FailedDelivery filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Cancelled',
        key: 'cancelled' as const,
        content: () => <Cancelled filters={sharedFilters} setFilters={setSharedFilters} />,
      },
      {
        label: 'Returned',
        key: 'returned' as const,
        content: () => <Returned filters={sharedFilters} setFilters={setSharedFilters} />,
      },
    ],
    [sharedFilters],
  );

  const handleTabChange = useCallback((index: number) => {
    setSelectedTabIndex(index);
  }, []);

  return (
    <Flex pt={6} direction="column" width="100%" fontFamily={'primary'} px={6}>
      <Tabs index={selectedTabIndex} onChange={handleTabChange}>
        <TabList justifyContent="space-around" width="100%" overflowX="auto" overflowY="hidden">
          {tabs.map((tab, idx) => (
            <Tab
              key={idx}
              width="100%"
              fontSize="sm"
              fontWeight="semibold"
              color="text.primary"
              _selected={{
                borderBottom: '2px solid',
                borderColor: 'brand.primary.DEFAULT',
                color: 'brand.primary.DEFAULT',
              }}
              gap={2}
              _hover={{ color: 'brand.primary.DEFAULT' }}
              _focus={{
                boxShadow: 'none',
                color: 'brand.primary.DEFAULT',
                borderBottom: '2px solid',
                borderColor: 'brand.primary.DEFAULT',
              }}
              _active={{ color: 'brand.primary.DEFAULT' }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
            >
              {tab.label}
              <Box
                minWidth="24px"
                minHeight="24px"
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                fontWeight="bold"
                color={selectedTabIndex === idx ? 'brand.primary.DEFAULT' : '#344054'}
                border="2px solid"
                pb={0}
                borderColor={selectedTabIndex === idx ? 'brand.primary.DEFAULT' : 'border.default'}
              >
                <Text lineHeight={1.2}>{countsLoading ? '…' : tabCounts[tab.key]}</Text>
              </Box>
            </Tab>
          ))}
        </TabList>

        <TabIndicator mt="-1.5px" height="2px" bg="brand.primary.DEFAULT" borderRadius="1px" />

        <TabPanels bg="background.secondary">
          {tabs.map((tab, idx) => (
            <TabPanel key={idx} px={0}>
              {selectedTabIndex === idx && tab.content()}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default withAuth(React.memo(AllOrders));
